'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import Image from 'next/image';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare form data for PHP handler
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('projectType', 'Contact Form');
      formDataToSend.append('budgetRange', 'Not specified');
      formDataToSend.append('timeline', 'Not specified');

      const response = await fetch('/contact-handler.php', {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', subject: '', message: '' });
        }, 3000);
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="from-card/0 via-background/10 to-background relative overflow-hidden bg-gradient-to-br py-20 md:py-40">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="bg-primary/5 absolute top-1/4 right-1/4 h-64 w-64 rounded-full blur-3xl" />
        <div className="bg-secondary/5 absolute bottom-1/4 left-1/4 h-64 w-64 rounded-full blur-3xl" />
        <div className="bg-accent/5 absolute top-1/2 left-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16 text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-primary/10 border-primary/20 mb-6 inline-flex items-center gap-3 rounded-full border px-6 py-3"
          >
            <Mail className="h-5 w-5 text-blue-500" />
            <span className="text-sm font-medium text-blue-400">Contact</span>
          </motion.div>
          <div className="from-primary to-foreground mb-4 bg-gradient-to-r bg-clip-text text-4xl leading-tight font-bold text-transparent md:text-5xl lg:text-6xl">
            Let&apos;s Build Something{' '}
            <span className="text-foreground">Amazing</span>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-foreground antonio mx-auto mb-4 max-w-2xl font-serif text-lg leading-relaxed md:max-w-4xl md:text-xl lg:text-2xl"
          >
            Ready to bring your vision to life? I&apos;d love to hear about your
            project and discuss how we can work together to create something
            extraordinary.
          </motion.p>
        </motion.div>

        {/* Contact Content */}
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="bg-card/50 border-border/50 rounded-2xl border p-8 shadow-xl backdrop-blur-sm">
              <div className="text-foreground mb-6 text-2xl font-bold">
                Send a Message
              </div>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-12 text-center"
                >
                  <CheckCircle className="mx-auto mb-4 h-16 w-16 text-green-500" />
                  <h4 className="text-foreground mb-2 text-xl font-semibold">
                    Message Sent!
                  </h4>
                  <p className="text-muted-foreground">
                    Thank you for reaching out. I&apos;ll get back to you soon!
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="name"
                        className="text-foreground mb-2 block text-sm font-medium"
                      >
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="bg-background/50 border-border focus:ring-primary/20 focus:border-primary w-full rounded-lg border px-4 py-3 transition-all duration-300 focus:ring-2"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="text-foreground mb-2 block text-sm font-medium"
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-background/50 border-border focus:ring-primary/20 focus:border-primary w-full rounded-lg border px-4 py-3 transition-all duration-300 focus:ring-2"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="subject"
                      className="text-foreground mb-2 block text-sm font-medium"
                    >
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="bg-background/50 border-border focus:ring-primary/20 focus:border-primary w-full rounded-lg border px-4 py-3 transition-all duration-300 focus:ring-2"
                      placeholder="What's this about?"
                    />
                  </div>

                  <div className="flex w-full flex-col gap-2">
                    <label
                      htmlFor="message"
                      className="text-foreground mb-2 block text-sm font-medium"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="border-border focus:ring-primary/20 focus:border-primary block w-full resize-none rounded-lg border px-8 py-3 transition-all duration-300 focus:ring-2"
                      style={{ backgroundColor: 'black !important' }}
                      placeholder="Tell me about your project..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    aria-label={
                      isSubmitting ? 'Sending message...' : 'Send message'
                    }
                    aria-describedby={
                      isSubmitting ? 'submitting-status' : undefined
                    }
                    className="from-primary to-primary/80 hover:from-primary/90 hover:to-primary flex w-full cursor-pointer items-center justify-center rounded-lg bg-gradient-to-r px-6 py-3 font-semibold text-white transition-all duration-300 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="flex w-full items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Sending...
                      </div>
                    ) : (
                      <div className="flex w-full items-center justify-center gap-2">
                        <Send className="h-4 w-4" />
                        Send Message
                      </div>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="space-y-8"
          >
            {/* Contact Methods */}
            <div className="space-y-6">
              <div className="text-foreground text-2xl font-bold">
                Get in Touch
              </div>

              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                  className="flex items-start space-x-4"
                >
                  <div className="bg-primary/10 border-primary/20 rounded-full border p-3">
                    <Phone className="text-primary h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-foreground mb-1 font-semibold">
                      Phone
                    </h4>
                    <a
                      href="tel:+17137327320"
                      className="text-muted-foreground hover:text-primary transition-colors duration-300"
                    >
                      <Image
                        width={20}
                        height={20}
                        src="/images/phone-number.svg"
                        alt="Phone number"
                        className="h-5 w-auto opacity-80 transition-opacity duration-300 hover:opacity-100"
                      />
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="flex items-start space-x-4"
                >
                  <div className="bg-primary/10 border-primary/20 rounded-full border p-3">
                    <MapPin className="text-primary h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-foreground mb-1 font-semibold">
                      Location
                    </h4>
                    <p className="text-muted-foreground">
                      Texas, United States
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Additional Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="from-primary/5 to-secondary/5 border-primary/20 rounded-2xl border bg-gradient-to-br p-6"
            >
              <h4 className="text-foreground mb-3 font-semibold">
                What to Expect
              </h4>
              <ul className="text-muted-foreground space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <div className="bg-primary h-2 w-2 rounded-full" />
                  Response within 24 hours
                </li>
                <li className="flex items-center gap-2">
                  <div className="bg-primary h-2 w-2 rounded-full" />
                  Free initial consultation
                </li>
                <li className="flex items-center gap-2">
                  <div className="bg-primary h-2 w-2 rounded-full" />
                  Detailed project proposal
                </li>
                <li className="flex items-center gap-2">
                  <div className="bg-primary h-2 w-2 rounded-full" />
                  Transparent pricing
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
